<template>
  <v-container class="result-container fill-height d-flex align-center justify-center">
    <v-card class="answer-result-card pa-1 pa-sm-6" elevation="6" max-width="1000" width="100%">
      <!-- Result Icon and Message -->
      <div class="text-center mb-4 mb-sm-6">
        <v-avatar
          :color="!quizRevealed ? 'primary' : isTimeout ? 'warning' : isCorrect ? 'success' : 'error'"
          class="mb-3 mb-sm-4 result-avatar"
        >
          <v-icon color="white" class="result-icon">
            {{
              !quizRevealed
                ? 'mdi-send-check'
                : isTimeout
                  ? 'mdi-clock-alert'
                  : isCorrect
                    ? 'mdi-check-circle'
                    : 'mdi-close-circle'
            }}
          </v-icon>
        </v-avatar>

        <h2
          class="text-h5 text-sm-h4 font-weight-bold mb-2"
          :class="!quizRevealed ? 'text-primary' : isTimeout ? 'text-warning' : isCorrect ? 'text-success' : 'text-error'"
        >
          {{ !quizRevealed ? '¡Respuesta enviada!' : isTimeout ? '¡Tiempo agotado!' : isCorrect ? '¡Respuesta Correcta!' : 'Respuesta Incorrecta' }}
        </h2>

        <p v-if="quizRevealed && isTimeout" class="text-body-2 text-medium-emphasis">
          El tiempo se agotó antes de que pudieras responder
        </p>
        <p v-if="!quizRevealed" class="text-body-2 text-medium-emphasis">
          Esperando que el profesor revele los resultados...
        </p>
      </div>

      <!-- Answer Details -->
      <v-row class="mb-4 mb-sm-6">
        <v-col cols="12" :sm="quizRevealed ? 4 : 6" class="py-2">
          <v-card
            variant="tonal"
            :color="isTimeout ? 'warning' : 'primary'"
            class="pa-3 pa-sm-4 text-center"
          >
            <div class="text-h6 text-sm-h5 font-weight-bold">
              {{ isTimeout ? 'Sin respuesta' : selectedOptionLabel }}
            </div>
            <div class="text-caption text-medium-emphasis">Tu respuesta</div>
          </v-card>
        </v-col>
        <v-col v-if="quizRevealed" cols="12" sm="4" class="py-2">
          <v-card variant="tonal" color="success" class="pa-3 pa-sm-4 text-center">
            <div class="text-h6 text-sm-h5 font-weight-bold">{{ correctOptionLabel }}</div>
            <div class="text-caption text-medium-emphasis">Respuesta correcta</div>
          </v-card>
        </v-col>
        <v-col cols="12" :sm="quizRevealed ? 4 : 6" class="py-2">
          <v-card variant="tonal" color="info" class="pa-3 pa-sm-4 text-center">
            <div class="text-h6 text-sm-h5 font-weight-bold">{{ formatTime(timeSpent) }}</div>
            <div class="text-caption text-medium-emphasis">Tiempo usado</div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Indicador de espera mientras el profesor no ha revelado resultados -->
      <div v-if="!quizRevealed" class="text-center my-6">
        <v-progress-circular indeterminate color="primary" size="48" class="mb-3" />
        <p class="text-body-2 text-medium-emphasis">
          Esperando que el profesor revele los resultados...
        </p>
      </div>

      <!-- Justification (solo visible cuando el profesor revela resultados) -->
      <v-card v-if="quizRevealed && (justification || justificationImageUrl)" variant="tonal" color="blue-grey-lighten-5" class="mb-4 mb-sm-6">
        <v-card-title
          class="d-flex align-center text-subtitle-1 text-sm-h6 pb-2 px-3 px-sm-4 pt-3 pt-sm-4"
        >
          <v-icon class="mr-2" color="primary" size="small">mdi-lightbulb-on</v-icon>
          <span class="text-medium-emphasis font-weight-bold">Justificación</span>
        </v-card-title>
        <v-card-text
          class="text-body-2 text-sm-body-1 text-medium-emphasis px-3 px-sm-4"
          style="text-align: justify"
        >
          <div v-if="justification">
            <MathRenderer :content="justification" />
          </div>
          <div v-if="justificationImageUrl" class="mt-3">
            <AuthenticatedImage
              :src="justificationImageUrl"
              max-height="300"
              image-class="rounded border"
            />
          </div>
        </v-card-text>
      </v-card>

      <!-- Action Button (solo visible cuando el profesor revela resultados) -->
      <div v-if="quizRevealed" class="text-center">
        <v-btn
          color="primary"
          :size="$vuetify.display.xs ? 'default' : 'large'"
          variant="elevated"
          @click="handleContinue"
          prepend-icon="mdi-home"
          class="px-6 px-sm-8"
          block-xs
        >
          Volver al Inicio
        </v-btn>
      </div>
    </v-card>
  </v-container>
</template>

<script setup>
import { computed } from 'vue'
import MathRenderer from '@/components/common/MathRenderer.vue'
import AuthenticatedImage from '@/components/common/AuthenticatedImage.vue'

const props = defineProps({
  answerData: {
    type: Object,
    required: true,
  },
  timeSpent: {
    type: Number,
    default: 0,
  },
  quizRevealed: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['continue'])

const isCorrect = computed(() => {
  return props.answerData?.correct || false
})

const isTimeout = computed(() => {
  return props.answerData?.timeout === true || props.answerData?.selected_option === null
})

const correctOptionLabel = computed(() => {
  return props.answerData?.correct_option || 'N/D'
})

const selectedOptionLabel = computed(() => {
  if (isTimeout.value) {
    return 'Sin respuesta'
  }
  return props.answerData?.selected_option || 'N/D'
})

const justification = computed(() => {
  return props.answerData?.justification || ''
})

const justificationImageUrl = computed(() => {
  return props.answerData?.justification_image_url || null
})

const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

const handleContinue = () => {
  emit('continue')
}
</script>

<style scoped>
.result-container {
  min-height: 90vh;
  padding: 16px;
}

.answer-result-card {
  border-radius: 16px;
  animation: fadeInUp 0.5s ease-out;
}

.result-avatar {
  width: 120px;
  height: 120px;
  animation: scaleIn 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.result-icon {
  font-size: 70px;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes scaleIn {
  from {
    transform: scale(0);
  }
  to {
    transform: scale(1);
  }
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .result-container {
    padding: 12px;
  }

  .answer-result-card {
    border-radius: 12px;
  }

  .result-avatar {
    width: 90px !important;
    height: 90px !important;
  }

  .result-icon {
    font-size: 50px !important;
  }
}

/* Small devices */
@media (max-width: 400px) {
  .result-container {
    padding: 8px;
  }

  .answer-result-card {
    border-radius: 8px;
  }

  .result-avatar {
    width: 80px !important;
    height: 80px !important;
  }

  .result-icon {
    font-size: 45px !important;
  }
}
</style>
